/* 個股工作室（Stock Studio）— 台股個股行情 / 估值 / 月營收 / 三大法人 / 籌碼 / 研究報告
 *
 * 前端移植自 D:\Code\Finlab 專案（frontend/src），配色改為入口網 editorial 色系
 * （HR.* tokens；Bento 漲=HR.up 墨綠、跌=HR.down 磚紅，與交易紀錄頁 PnL 語意一致；
 *  Expressive 外殼改台股慣例漲紅跌綠 GxT.color.mktUp/mktDown，見 ssUp/ssDown、SPEC §1.2）。
 *
 * 圖表庫（static/vendor/，UMD globals，index.html 於 dist scripts 前載入）：
 *   - lightweight-charts 4.2.3 standalone → window.LightweightCharts（K 線 + 成交量）
 *   - recharts 2.15.4 UMD → window.Recharts（需 window.PropTypes；月營收/估值/法人/籌碼）
 *
 * 系列色（dataviz 驗證過的 3 槽：chroma/CVD/對比全過）：
 *   s1 #08917F（teal，品牌 accent 圖表級變體）· s2 #B06A14（amber）· s3 #3F6BAE（slate）
 *
 * 資料源（2026-09-02 起，Ray 裁定）：除研究報告外全部改走 /api/quantdata 直取
 * （routes_quantdata.py catch-all 唯讀代理 → quantdata.lan DuckDB），查詢契約見下方
 * qd() 註解。各卡片對應 view：
 *   搜尋 / 頁首名稱產業        security_attrs（全表快取，前端過濾）
 *   摘要磚（qdSummary 組裝）   security_attrs + tw_stock_trading_attrs_daily +
 *                              tw_stock_bars + tw_stock_valuation_daily +
 *                              revenue_monthly + tw_inst_stock_daily + tw_chip_dist_daily
 *   股價 K 線                  tw_stock_bars（open..volume + adj_*，原始/還原同一 view）
 *   估值（PER/PBR/殖利率）     tw_stock_valuation_daily（per_tej / pbr_tej / cash_div_yield_pct / per）
 *   月營收                     revenue_monthly（fiscal_month / revenue_monthly_ktwd / yoy）
 *   三大法人                   tw_inst_stock_daily
 *   占集保庫存數比例           tw_chip_dist_daily（週資料）
 *   融資融券                   tw_margin_daily
 *   主力分點買賣超             tw_broker_branch_daily（每日前 15 名，落後數日）
 *   主力籌碼集中度             tw_broker_branch_conc_daily（上游預算 5/20/60d）
 *   股票期貨大額交易人         stock_futures_continuous_d + futures_specs + tw_futures_large_trader_daily
 *
 * 研究報告仍走 finlab 代理（quantdata 無 auto_acr 報告資料）：
 *   GET /api/finlab/stocks/{s}/reports          → { reports: [...] }（auto_acr 比對）
 *   GET /api/finlab/reports/{job_id}?lang=zh    → 報告全文 JSON {markdown, title, ...}（站內 Modal 渲染）
 *
 * 效能：09-02 冷掃（上游冷快取全表 count(*)）曾 28–57s/view，gs-scraper #45/#46
 * 修掉根因後熱查詢 ~0.2s、最壞巡檢干擾期 ~10s → 前端逾時 30s（QD_TIMEOUT_MS）
 * + 併發閘 3；詳見 QD_TIMEOUT_MS 定義處註解（含部署順序前提）。
 *
 * 路由：#/stock-studio（首頁搜尋）· #/stock-studio/2330（個股深連結）·
 *       #/stock-studio/2330/chips（第三段=分頁：fund 基本面（預設）/ chips 籌碼面 /
 *       reports 研究報告；F5、轉貼連結停在同一分頁；未知分頁值回落基本面）。
 * 權限：員工 + analyst + non_staff 可用（/api/quantdata 在其白名單）；
 *       ⚠ analyst_lite 白名單刻意無 /api/quantdata → 除研究報告外整頁 403
 *       （RetryNote 顯示無權限、不給重試），與 2026-09-01 前行為一致。
 */

(function () {

/* ─────────────  數字格式化（沿用 Finlab format.ts，IIFE 內不污染全域）───────────── */

function fmtNum(v, digits = 2) {
  if (v == null) return '—';
  return v.toLocaleString('zh-TW', { maximumFractionDigits: digits });
}
/** 元 → 兆/億/萬 */
function fmtTWD(v) {
  if (v == null) return '—';
  const abs = Math.abs(v);
  if (abs >= 1e12) return `${(v / 1e12).toFixed(2)} 兆`;
  if (abs >= 1e8) return `${(v / 1e8).toFixed(1)} 億`;
  if (abs >= 1e4) return `${(v / 1e4).toFixed(0)} 萬`;
  return fmtNum(v, 0);
}
/** 千元 → 兆/億 */
function fmtKTWD(v) { return v == null ? '—' : fmtTWD(v * 1000); }
/** 千元 → 圖表 Y 軸刻度用的精簡格式（窄螢幕下「6000.0 億」會擠出軸區） */
function fmtKTWDTick(v) {
  if (v == null) return '';
  const twd = v * 1000, abs = Math.abs(twd);
  if (abs >= 1e12) return `${(twd / 1e12).toFixed(1)}兆`;
  if (abs >= 1e8) return `${Math.round(twd / 1e8).toLocaleString('zh-TW')}億`;
  return fmtNum(twd, 0);
}
function fmtPct(v, digits = 2) {
  if (v == null) return '—';
  return `${v >= 0 ? '+' : ''}${v.toFixed(digits)}%`;
}
function fmtPctPlain(v, digits = 1) { return v == null ? '—' : `${v.toFixed(digits)}%`; }
function fmtLots(v) {
  if (v == null) return '—';
  if (Math.abs(v) >= 1e4) return `${(v / 1e4).toFixed(1)} 萬張`;
  return `${fmtNum(v, 0)} 張`;
}
/* Market-direction colours (SPEC §1.2) — Bento: HR.up 墨綠 / HR.down 磚紅 (unchanged);
 * Expressive: 台股慣例漲紅跌綠 (GxT.color.mktUp / mktDown). Render-time only (never cache).
 * gx-mkt:ss:begin (JSX-free; tests/test_gx_mkt_colors.cjs evaluates this block) */
function ssUp() { return uiPick(HR.up, GxT.color.mktUp); }
function ssDown() { return uiPick(HR.down, GxT.color.mktDown); }
// Concrete-colour versions for the canvas chart (lightweight-charts cannot resolve var(--gx-*)):
// Bento → the HR literal unchanged; Expressive → the current theme's value (GxCore.hex).
function ssUpHex() { return GxCore.hex(ssUp()); }
function ssDownHex() { return GxCore.hex(ssDown()); }
function _ssAlpha(hex, a) {
  const n = parseInt(String(hex).slice(1), 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${a})`;
}
// K 線成交量柱：Bento 維持 SS.upSoft / SS.downSoft 原字面值
function ssVol(up) {
  return up ? uiPick(SS.upSoft, _ssAlpha(GxCore.hex(GxT.color.mktUp), 0.35)) : uiPick(SS.downSoft, _ssAlpha(GxCore.hex(GxT.color.mktDown), 0.35));
}
function signColor(v) {
  if (v == null || v === 0) return HR.ink;
  return v > 0 ? ssUp() : ssDown();
}
/* gx-mkt:ss:end */
/** '2026-07-01' → '2026/07' */
function fmtMonth(d) { return (d || '').slice(0, 7).replace('-', '/'); }
const fmtDateTick = fmtMonth;

/* ─────────────  API 層 — columnar Series → 物件陣列  ───────────── */

function toObjects(s) {
  return s.rows.map((row) => Object.fromEntries(s.columns.map((c, i) => [c, row[i]])));
}
const qs = (params) => {
  const p = Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '');
  // Keys are encoded too (defensive; the finlab params are plain ASCII anyway).
  return p.length ? '?' + p.map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`).join('&') : '';
};
/* ── 帶逾時的 GET ──
 * api()（hr-shell.jsx）是裸 fetch 無逾時，上游冷啟/排隊會把區塊卡死在「載入中」；
 * 這裡用 AbortController 設上限，逾時落到錯誤態讓使用者按重試。
 * 錯誤形狀比照 api()：{status, detail}，逾時另帶 timeout: true（status: 0）。 */
const FIN_TIMEOUT_MS = 12000; // finlab 代理端 30s（routes_finlab.py），前端先斷

async function apiGetT(path, timeoutMs) {
  const ctl = new AbortController();
  const timer = window.setTimeout(() => ctl.abort(), timeoutMs);
  const headers = { Accept: 'application/json' };
  const token = getToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  try {
    const res = await fetch(API_BASE + path, { method: 'GET', headers, signal: ctl.signal });
    let data = null;
    const text = await res.text();
    if (text) { try { data = JSON.parse(text); } catch { data = { raw: text }; } }
    if (!res.ok) {
      let detail = data && data.detail;
      if (typeof detail !== 'string') detail = (detail && JSON.stringify(detail)) || `HTTP ${res.status}`;
      throw { status: res.status, detail };
    }
    return data;
  } catch (e) {
    if (e && e.name === 'AbortError') throw { status: 0, detail: '載入逾時', timeout: true };
    throw e;
  } finally {
    window.clearTimeout(timer);
  }
}

const finGet = (path) => apiGetT(path, FIN_TIMEOUT_MS);
// 僅研究報告仍走 finlab（quantdata 無 auto_acr 報告資料），其餘全走 qd() 直取。
const fin = {
  reports: (s) => finGet(`/finlab/stocks/${s}/reports`),
};

function isoDaysAgo(days) {
  const d = new Date(); d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
}

/* ── QUANTDATA 直取 ──
 * 上游 quantdata.lan 的 DuckDB，走既有 /api/quantdata 代理（catch-all 唯讀轉發、
 * Bearer JWT，routes_quantdata.py）。查詢契約：`<欄位名>=<值>` 等值過濾（支援中文
 * 欄名）、select=（逗號分隔）、order=（僅升冪）、start=/end=（繫結該 view 的日期欄）、
 * limit=/offset=。回傳 {columns, rows} 欄式（沿用 toObjects）。
 * 注意：
 *   - rows 不保證排序 → 一律 qdRows() 轉日期後自行升冪排序
 *   - 日期是 HTTP-date 字串（"Wed, 19 Aug 2026 …"）→ qdDate() 轉 YYYY-MM-DD
 *   - 代理會剝除名為 token 的 query 參數（防 JWT 外洩），欄位過濾勿用該名
 *   - analyst_lite 白名單刻意無 /api/quantdata → 403（RetryNote 顯示無權限、不給重試） */
/* 逾時：原 120s 是照上游冷快取全表 count(*)（單 view 28–57s、valuation 曾 >120s）
 * 訂的；gs-scraper #45/#46 修掉該根因後資料查詢 ~0.2s、最壞背景巡檢干擾期 ~10s
 * → 上限 30s，逾時仍落錯誤態給重試。部署順序：須等上游 #46 跑過一輪 build-catalog
 * （每晚 daily_refresh）生效後再上本值。 */
const QD_TIMEOUT_MS = 30000;
/* 併發閘：同分頁多卡同時打 quantdata 會讓 DuckDB 排隊、把逾時整段吃掉。
 * 前端限流「同時最多 3 個」，其餘排隊 — 排隊時間不計入逾時（逾時計時從真正
 * 發出請求才開始），整分頁載完只是稍慢、不再成批逾時。 */
const QD_MAX_CONCURRENT = 3;
let _qdActive = 0;
const _qdWaiters = [];
function _qdRelease() {
  _qdActive--;
  const next = _qdWaiters.shift();
  if (next) next();
}
async function qd(view, params) {
  if (_qdActive >= QD_MAX_CONCURRENT) {
    await new Promise((resolve) => _qdWaiters.push(resolve));
  }
  _qdActive++;
  try {
    return await apiGetT(`/quantdata/api/v1/data/${view}${qs(params || {})}`, QD_TIMEOUT_MS);
  } finally {
    _qdRelease();
  }
}

function qdDate(v) {
  if (!v) return null;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? String(v).slice(0, 10) : d.toISOString().slice(0, 10);
}
function qdRows(s, dateCol) {
  const rows = toObjects(s).map((r) => ({ ...r, [dateCol]: qdDate(r[dateCol]) }));
  rows.sort((a, b) => (a[dateCol] < b[dateCol] ? -1 : a[dateCol] > b[dateCol] ? 1 : 0));
  return rows;
}

/* ── 搜尋 — security_attrs 全表快取（~3.4k 列），前端過濾 ──
 * quantdata API 只有等值過濾、無模糊搜尋 → 首次搜尋抓一次全表（select 名稱/產業/
 * 下市日），之後純前端比對：代碼前綴 / 名稱包含，代碼完全相符排最前。
 * ⌘K 面板經 window.StockStudioSearch 共用本快取與排序（tests/test_cmdk_search.cjs
 * 直接抽出 stripIndustryCode…qdSearch 這段測試，勿在其間加入依賴其他 IIFE 名稱的程式）。 */
function stripIndustryCode(v) { return (v || '').replace(/^[A-Z]+\d+\s*/, ''); }
let _secListPromise = null;
function secList() {
  if (!_secListPromise) {
    _secListPromise = qd('security_attrs', {
      select: 'stock_id,name_zh,main_industry_zh,delist_date', limit: 10000,
    }).then((s) => toObjects(s).map((r) => ({
      symbol: r.stock_id,
      name: r.name_zh || '',
      industry: stripIndustryCode(r.main_industry_zh),
      listing_status: r.delist_date ? 'delisted' : 'listed',
    })));
    _secListPromise.catch(() => { _secListPromise = null; }); // 失敗不快取，下次重抓
  }
  return _secListPromise;
}
async function qdSearch(q) {
  const list = await secList();
  const hits = list
    .filter((r) => r.symbol.startsWith(q) || r.name.includes(q))
    .sort((a, b) =>
      (b.symbol === q) - (a.symbol === q)
      || (b.symbol.startsWith(q)) - (a.symbol.startsWith(q))
      || (b.listing_status === 'listed') - (a.listing_status === 'listed')
      || a.symbol.localeCompare(b.symbol))
    .slice(0, 12);
  return { results: hits };
}

/* ── 摘要磚 — 7 個 view 組裝（原 finlab StockSummary 的等價物）──
 * security_attrs 是身分依據：查無列 → 拋 404（StockDetail 顯示查無此代碼）。
 * 其餘 view 個別失敗只讓對應欄位顯示 '—'，不整頁陣亡（.catch(null)）。 */
async function qdSummary(symbol) {
  const soft = (p) => p.catch(() => null);
  const [attrsS, taS, barsS, valS, revS, instS, chipS] = await Promise.all([
    qd('security_attrs', {
      stock_id: symbol, limit: 1,
      select: 'stock_id,name_zh,main_industry_zh,sub_industry_zh,delist_date',
    }),
    soft(qd('tw_stock_trading_attrs_daily', {
      stock_id: symbol, start: isoDaysAgo(10),
      select: 'trading_date,market,is_attention,is_disposition,is_suspended,is_twn50,is_msci,is_hdiv',
    })),
    soft(qd('tw_stock_bars', {
      symbol, start: isoDaysAgo(14), select: 'trading_date,close,exchange',
    })),
    soft(qd('tw_stock_valuation_daily', {
      stock_id: symbol, start: isoDaysAgo(14),
      select: 'trading_date,per,per_tej,pbr_tej,cash_div_yield_pct,market_cap',
    })),
    soft(qd('revenue_monthly', {
      stock_id: symbol, start: isoDaysAgo(120), select: 'fiscal_month,revenue_yoy_growth_pct',
    })),
    soft(qd('tw_inst_stock_daily', {
      stock_id: symbol, start: isoDaysAgo(14),
      select: 'trading_date,foreign_net_lot,foreign_hold_pct',
    })),
    soft(qd('tw_chip_dist_daily', {
      stock_id: symbol, start: isoDaysAgo(30), select: 'trading_date,pct_over_400',
    })),
  ]);

  const attrs = toObjects(attrsS)[0];
  if (!attrs) throw { status: 404, detail: 'unknown symbol' };

  const yes = (v) => v === 'Y';
  const ta = taS ? qdRows(taS, 'trading_date').pop() : null;
  const bars = barsS ? qdRows(barsS, 'trading_date') : [];
  const last = bars[bars.length - 1] || null;
  const prev = bars[bars.length - 2] || null;
  const vals = valS ? qdRows(valS, 'trading_date') : [];
  const val = vals[vals.length - 1] || null;
  const perExRows = vals.filter((r) => r.per != null);
  const rev = revS ? qdRows(revS, 'fiscal_month').pop() : null;
  const inst = instS ? qdRows(instS, 'trading_date') : [];
  const inst5 = inst.slice(-5);
  const chip = chipS ? qdRows(chipS, 'trading_date').pop() : null;
  const exch = (ta && ta.market) || (last && last.exchange) || '';

  return {
    symbol,
    name: attrs.name_zh,
    industry: stripIndustryCode(attrs.main_industry_zh) || null,
    sub_industry: stripIndustryCode(attrs.sub_industry_zh) || null,
    listing_status: attrs.delist_date ? 'delisted' : 'listed',
    market: /^TWSE$/i.test(exch) ? 'twse' : /^TPEX$/i.test(exch) ? 'tpex' : exch || null,
    is_attention: yes(ta && ta.is_attention),
    is_disposition: yes(ta && ta.is_disposition),
    is_suspended: yes(ta && ta.is_suspended),
    is_twn50: yes(ta && ta.is_twn50),
    is_msci: yes(ta && ta.is_msci),
    is_hdiv: yes(ta && ta.is_hdiv),
    price_date: last ? last.trading_date : null,
    close: last ? last.close : null,
    change: last && prev && last.close != null && prev.close != null ? last.close - prev.close : null,
    change_pct: last && prev && last.close != null && prev.close ? ((last.close - prev.close) / prev.close) * 100 : null,
    per_tej: val ? val.per_tej : null,
    pbr_tej: val ? val.pbr_tej : null,
    cash_div_yield_pct: val ? val.cash_div_yield_pct : null,
    market_cap: val ? val.market_cap : null,
    per_ex: perExRows.length ? perExRows[perExRows.length - 1].per : null,
    latest_revenue_yoy: rev ? rev.revenue_yoy_growth_pct : null,
    latest_revenue_month: rev ? rev.fiscal_month : null,
    foreign_net_5d: inst5.length ? inst5.reduce((acc, r) => acc + (r.foreign_net_lot || 0), 0) : null,
    foreign_hold_pct: inst.length ? inst[inst.length - 1].foreign_hold_pct : null,
    pct_over_400: chip ? chip.pct_over_400 : null,
    chip_date: chip ? chip.trading_date : null,
  };
}

/* ─────────────  圖表色 — 入口網 palette（見檔頭；已跑 dataviz 驗證器）───────────── */

const SS = {
  s1: '#08917F',
  s2: '#B06A14',
  s3: '#3F6BAE',
  upSoft: 'rgba(11, 95, 90, 0.35)',   // K 線成交量（HR.up 半透明）
  downSoft: 'rgba(155, 51, 48, 0.35)',
};

/* ─────────────  路由（hash 第二段 = 股票代碼，第三段 = 分頁）─────────────
 * #/stock-studio/2330 → 基本面（預設）；#/stock-studio/2330/chips → 籌碼面。
 * 分頁記在網址上：F5 / 轉貼連結會停在同一分頁。 */

const TABS = [
  { k: 'fund', label: '基本面' },
  { k: 'chips', label: '籌碼面' },
  { k: 'reports', label: '研究報告' },
];

function parseRoute() {
  const m = (window.location.hash || '').match(/^#\/stock-studio\/([0-9A-Za-z]+)(?:\/([a-z]+))?/);
  if (!m) return { symbol: null, tab: 'fund' };
  return { symbol: m[1], tab: TABS.some((t) => t.k === m[2]) ? m[2] : 'fund' };
}
function gotoStock(sym) { window.location.hash = `#/stock-studio/${sym}`; }
function tabHash(symbol, k) { return k === 'fund' ? `#/stock-studio/${symbol}` : `#/stock-studio/${symbol}/${k}`; }

/* ─────────────  搜尋框  ───────────── */

/* onPick（選填）：選中個股後（導向個股工作室之後）回呼 hit 物件
 * {symbol, name, ...} — 分析工作台（hr-analyst-hub.jsx）用來記「最近使用」。 */
function StockSearchBox({ autoFocus = false, compact = false, onPick = null }) {
  const [q, setQ] = React.useState('');
  const [hits, setHits] = React.useState([]);
  const [open, setOpen] = React.useState(false);
  const [active, setActive] = React.useState(0);
  const box = React.useRef(null);
  const timer = React.useRef();

  React.useEffect(() => {
    window.clearTimeout(timer.current);
    if (!q.trim()) { setHits([]); setOpen(false); return; }
    timer.current = window.setTimeout(async () => {
      try {
        const r = await qdSearch(q.trim());
        setHits((r && r.results) || []);
        setOpen(true);
        setActive(0);
      } catch { /* 搜尋失敗靜默 */ }
    }, 220);
    return () => window.clearTimeout(timer.current);
  }, [q]);

  React.useEffect(() => {
    const onDoc = (e) => { if (box.current && !box.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const go = (sym) => {
    setOpen(false); setQ(''); gotoStock(sym);
    if (onPick) {
      const hit = hits.find((h) => h.symbol === sym);
      try { onPick(hit || { symbol: sym }); } catch (e) { /* 回呼失敗不影響導向 */ }
    }
  };

  return (
    <div ref={box} style={{ position: 'relative', width: '100%', maxWidth: compact ? 400 : 520 }}>
      <input
        value={q}
        autoFocus={autoFocus}
        placeholder="搜尋股票代碼或名稱（例：2330、台積電）"
        onChange={(e) => setQ(e.target.value)}
        onFocus={() => hits.length && setOpen(true)}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown') { e.preventDefault(); setActive((a) => Math.min(a + 1, hits.length - 1)); }
          if (e.key === 'ArrowUp') { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
          if (e.key === 'Enter' && hits[active]) go(hits[active].symbol);
          if (e.key === 'Escape') setOpen(false);
        }}
        style={uiStyle({
          width: '100%', padding: compact ? '8px 14px' : '11px 16px',
          border: `1px solid ${HR.rule}`, borderRadius: 8,
          background: HR.surface, color: HR.ink,
          fontSize: FontSizes.base, fontFamily: HR.sans, outline: 'none',
          boxSizing: 'border-box',
        }, {
          // outlined 搜尋列（= inputStyle GX 版：card 底＋1px ol 框；hover／focus 外框由 gx-css-pages 處理）
          minHeight: compact ? 44 : 52, padding: compact ? '10px 16px' : '14px 18px',
          border: `1px solid ${GxT.color.ol}`, borderRadius: compact ? GxT.radius.btn : GxT.radius.btnLg,
          background: GxT.color.card, fontSize: 15,
        })}
        onFocusCapture={(e) => { e.currentTarget.style.borderColor = HR.accent; }}
        onBlurCapture={(e) => {
          // GX: back to ol (HR.rule is patched to the light olv, which would drop the edge below 3:1)
          e.currentTarget.style.borderColor = uiPick(HR.rule, GxT.color.ol);
        }}
      />
      {open && hits.length > 0 && (
        <div style={uiStyle({
          position: 'absolute', top: 'calc(100% + 6px)', left: 0, right: 0, zIndex: 40,
          background: HR.surface, border: `1px solid ${HR.rule}`, borderRadius: 8,
          boxShadow: '0 8px 28px rgba(14, 20, 17, 0.12)', overflow: 'hidden',
        }, { border: 'none', borderRadius: GxT.radius.card, boxShadow: GxT.shadow.pop, padding: 6 })}>
          {hits.map((h, i) => (
            <button
              key={h.symbol}
              onMouseEnter={() => setActive(i)}
              onClick={() => go(h.symbol)}
              style={uiStyle({
                display: 'flex', gap: 10, alignItems: 'baseline', width: '100%',
                padding: '9px 14px', border: 0, textAlign: 'left', cursor: 'pointer',
                background: i === active ? HR.accentSoft : 'transparent',
                color: HR.ink, fontSize: FontSizes.xs, fontFamily: HR.sans,
              }, {
                // mockup .pal-top：選中列 s3 底、圓角 10
                padding: '10px 14px', borderRadius: GxT.radius.btnSm,
                background: i === active ? GxT.color.s3 : 'transparent', fontSize: 14,
              })}
            >
              <span style={{ fontWeight: 700, fontFamily: HR.mono, fontVariantNumeric: 'tabular-nums' }}>{h.symbol}</span>
              <span>{h.name}</span>
              {h.listing_status === 'delisted' && (
                <span style={uiStyle({
                  borderRadius: 99, padding: '0 8px', fontSize: FontSizes.trading_md,
                  background: '#F5EDE3', color: HR.warn, fontWeight: 600,
                }, {
                  borderRadius: GxT.radius.chipSm, padding: '1px 8px', fontWeight: GxT.weight.label,
                  background: GxCore.tone('warn').bg, color: GxCore.tone('warn').fg,
                })}>已下市</span>
              )}
              <span style={uiStyle(
                { marginLeft: 'auto', color: HR.inkFaint, fontSize: FontSizes.xxs },
                { color: GxT.color.onV },
              )}>{h.industry || ''}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* ─────────────  首頁（無代碼時）— 搜尋 hero + 熱門捷徑  ───────────── */

const HOT_STOCKS = [
  ['2330', '台積電'], ['2317', '鴻海'], ['2454', '聯發科'], ['2308', '台達電'],
  ['2382', '廣達'], ['2891', '中信金'], ['2603', '長榮'], ['3231', '緯創'],
];

function StudioHome() {
  return (
    <div style={uiStyle({ textAlign: 'center', padding: '72px 0 40px' }, { padding: '56px 0 40px' })}>
      <div style={uiStyle({
        fontSize: FontSizes.trading_lg, letterSpacing: '0.22em', textTransform: 'uppercase',
        color: HR.inkFaint, marginBottom: 14,
      }, {
        // mockup .greet .gdate：主色 700、無大寫字距
        fontSize: GxT.size.label, letterSpacing: 0, textTransform: 'none',
        color: GxT.color.p, fontWeight: GxT.weight.label, marginBottom: 6,
      })}>Stock Studio</div>
      <h1 style={uiStyle({
        fontFamily: HR.serif, fontSize: 34, fontWeight: 400,
        letterSpacing: '-0.015em', margin: '0 0 10px', color: HR.ink,
      }, {
        fontSize: 'clamp(30px, 4.4vw, 40px)', fontWeight: GxT.weight.display,
        letterSpacing: '-0.5px', lineHeight: 1.12,
      })}>個股工作室</h1>
      <p style={uiStyle(
        { color: HR.inkSoft, fontSize: FontSizes.sm, margin: '0 0 30px' },
        { fontSize: GxT.size.body },
      )}>
        台股個股行情 · 估值 · 月營收 · 三大法人 · 籌碼集中度 · 公司研究報告
      </p>
      <div style={{ display: 'flex', justifyContent: 'center' }}>
        <StockSearchBox autoFocus />
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'center', marginTop: 26 }}>
        {HOT_STOCKS.map(([s, n]) => (
          <a
            key={s} href={`#/stock-studio/${s}`}
            onMouseEnter={(e) => { e.currentTarget.style.borderColor = HR.accent; e.currentTarget.style.color = HR.accent; }}
            onMouseLeave={(e) => { e.currentTarget.style.borderColor = HR.rule; e.currentTarget.style.color = HR.inkSoft; }}
            style={uiStyle({
              border: `1px solid ${HR.rule}`, borderRadius: 99, padding: '5px 14px',
              color: HR.inkSoft, fontSize: FontSizes.xs, background: HR.surface,
              textDecoration: 'none', transition: 'all 120ms',
            }, {
              // mockup .per：外框 chip、圓角 8
              borderRadius: GxT.radius.chip, padding: '6px 14px', fontSize: 13,
              fontWeight: GxT.weight.medium, background: 'transparent',
            })}
          >{s} {n}</a>
        ))}
      </div>
      <div style={uiStyle({ color: HR.inkFaint, fontSize: FontSizes.xxs, marginTop: 48 }, { fontSize: GxT.size.meta })}>
        資料來源：公司量化資料庫（quantdata）· TEJ / 集保 · 每日盤後同步 · 報告：auto_acr
      </div>
    </div>
  );
}

/* ─────────────  共用資料 hook 與圖表卡殼  ───────────── */

/* useAsync：loader 回什麼就存什麼。
 * retry() bump nonce 重跑 loader — 逾時/失敗後由 RetryNote 觸發。 */
function useAsync(loader, deps) {
  const [data, setData] = React.useState(null);
  const [err, setErr] = React.useState(null); // null | {status, detail, timeout}
  const [nonce, setNonce] = React.useState(0);
  React.useEffect(() => {
    let dead = false;
    setData(null); setErr(null);
    loader().then((d) => !dead && setData(d)).catch((e) => !dead && setErr(e || { detail: '載入失敗' }));
    return () => { dead = true; };
  }, [...deps, nonce]);
  const retry = React.useCallback(() => setNonce((n) => n + 1), []);
  return { data, err, retry };
}

/* Error state + retry button. 403 = endpoint not on this account's whitelist —
 * retrying cannot succeed, so no button. */
function RetryNote({ err, onRetry }) {
  const denied = err && err.status === 403;
  const text = denied ? '此帳號無此資料存取權限'
    : err && err.timeout ? '載入逾時 — 資料庫查詢排隊中，稍候可重試'
    : '載入失敗';
  return (
    <div style={{ textAlign: 'center', padding: '30px 0' }}>
      <div style={{ color: denied ? HR.inkFaint : HR.down, fontSize: FontSizes.xs }}>{text}</div>
      {!denied && onRetry && (
        <button
          onClick={onRetry}
          style={uiStyle({
            marginTop: 10, border: `1px solid ${HR.rule}`, borderRadius: 6,
            background: HR.surface, color: HR.accent, padding: '4px 16px',
            fontSize: FontSizes.xxs, cursor: 'pointer', fontFamily: HR.sans,
          }, {
            // tonal 小按鈕（mockup .btn.tonal.sm）
            minHeight: 36, padding: '0 16px', border: 'none', borderRadius: GxT.radius.btnSm,
            background: GxT.color.sc, color: GxT.color.onSc, fontSize: 13, fontWeight: GxT.weight.label,
          })}
        >重試</button>
      )}
    </div>
  );
}

/* Expressive 切換鈕（mockup .per：外框 chip 圓角 8；選中 = secondary container 圓角 10）。
 * 只當 uiStyle 的 GX patch 用（Bento 不讀）。 */
function ssSegGx(on) {
  return {
    minHeight: 32, padding: '0 12px', fontSize: 13, fontFamily: GxT.font,
    border: `1px solid ${on ? GxT.color.sc : GxT.color.olv}`,
    borderRadius: on ? GxT.radius.btnSm : GxT.radius.chip,
    background: on ? GxT.color.sc : 'transparent',
    color: on ? GxT.color.onSc : GxT.color.onV,
    fontWeight: on ? GxT.weight.label : GxT.weight.medium,
  };
}

function ChartCard({ title, sub, state, right, children }) {
  return (
    <div style={uiStyle({
      background: HR.surface, border: `1px solid ${HR.rule}`, borderRadius: 10,
      padding: '18px 20px', marginBottom: 14,
    }, { border: 'none', borderRadius: GxT.radius.card, padding: '20px', marginBottom: 16 })}>
      <div style={uiStyle(
        { display: 'flex', alignItems: 'baseline', gap: 12 },
        { flexWrap: 'wrap', rowGap: 4 },
      )}>
        <h2 style={uiStyle({
          fontFamily: HR.serif, fontSize: FontSizes.md, fontWeight: 500,
          margin: 0, color: HR.ink, letterSpacing: '-0.01em',
        }, { fontSize: GxT.size.h3, fontWeight: GxT.weight.title, letterSpacing: 0 })}>{title}</h2>
        <span style={uiStyle({ color: HR.inkFaint, fontSize: FontSizes.xxs }, { fontSize: 13 })}>{sub}</span>
        {right && <div style={{ marginLeft: 'auto' }}>{right}</div>}
      </div>
      {/* overflow:hidden 全域保險：圖表若因 observer 節流等原因暫留舊尺寸，
          只會被裁在卡片內，不會爆出版面 */}
      <div style={{ marginTop: 12, overflow: 'hidden' }}>
        {state && state.err ? <RetryNote err={state.err} onRetry={state.retry} />
          : state && state.data === null ? <StateNote text="載入中…" />
          : state && state.data.length === 0 ? <StateNote text="無資料" />
          : children}
      </div>
    </div>
  );
}

function StateNote({ text, tone }) {
  return (
    <div style={{
      color: tone === 'err' ? HR.down : HR.inkFaint, textAlign: 'center',
      padding: '36px 0', fontSize: FontSizes.xs,
    }}>{text}</div>
  );
}

/* ─────────────  recharts 系列圖（月營收 / 估值 / 三大法人 / 籌碼）─────────────
 * 規約：單一 y 軸；量值與百分比不同尺度 → 上下小倍數共用 x 軸；線 2px 無點；bar 頂 2px 圓角。 */

/* 軸／tooltip props 一律是函式、render 時才讀 token：Expressive 外殼在 eval 期 patch
 * HR.inkFaint／HR.sans，模組頂層常數會把 patch 後的值凍住、降級後不還原
 * （plan-expressive-inner-pages.md §2(d)-2）。Bento 回傳值與原物件逐 key 相同；
 * GX 只改 tooltip chrome（圓角 10、無框、陰影、字型），系列色 SS 不動。
 * gx-lazy:SS_chart_props:begin（JSX-free；tests/test_gx_p3_pages.cjs 以 vm 擷取） */
function ssAxisProps() {
  return { stroke: GxCore.hex(HR.inkFaint), fontSize: 11, tickLine: false, axisLine: { stroke: GxCore.hex(HR.rule) } };
}
function ssTooltipProps() {
  return {
    contentStyle: uiStyle({
      background: HR.surface, border: `1px solid ${HR.rule}`, borderRadius: 8,
      fontSize: 12, fontFamily: HR.sans,
    }, { border: 'none', borderRadius: GxT.radius.btnSm, boxShadow: GxT.shadow.pop, fontFamily: GxT.font }),
    labelStyle: { color: HR.inkSoft },
  };
}
/* gx-lazy:SS_chart_props:end */

function RevenueChart({ symbol }) {
  const RC = window.Recharts;
  GxCore.useTheme();  // recharts SVG attributes take concrete colours → re-render on theme change
  const st = useAsync(() => qd('revenue_monthly', {
    stock_id: symbol, start: isoDaysAgo(5 * 365),
    select: 'fiscal_month,revenue_monthly_ktwd,revenue_yoy_growth_pct',
  }).then((s) => qdRows(s, 'fiscal_month')), [symbol]);
  const data = (st.data || []).map((r) => ({ ...r, revenue_ktwd: r.revenue_monthly_ktwd, m: fmtMonth(r.fiscal_month) }));
  return (
    <ChartCard title="月營收" sub="近 5 年單月營收與年增率（上下共用時間軸，各自尺度）" state={st}>
      <RC.ResponsiveContainer width="100%" height={190}>
        <RC.BarChart data={data} margin={{ top: 4, right: 8, left: 8, bottom: 0 }}>
          <RC.CartesianGrid stroke={GxCore.hex(HR.ruleSoft)} vertical={false} />
          <RC.XAxis dataKey="m" {...ssAxisProps()} minTickGap={40} />
          <RC.YAxis {...ssAxisProps()} tickFormatter={fmtKTWDTick} width={54} />
          <RC.Tooltip {...ssTooltipProps()} formatter={(v) => [fmtKTWD(v), '單月營收']} />
          <RC.Bar dataKey="revenue_ktwd" name="單月營收" fill={SS.s1} radius={[2, 2, 0, 0]} />
        </RC.BarChart>
      </RC.ResponsiveContainer>
      <RC.ResponsiveContainer width="100%" height={130}>
        <RC.LineChart data={data} margin={{ top: 4, right: 8, left: 8, bottom: 0 }}>
          <RC.CartesianGrid stroke={GxCore.hex(HR.ruleSoft)} vertical={false} />
          <RC.XAxis dataKey="m" {...ssAxisProps()} minTickGap={40} />
          <RC.YAxis {...ssAxisProps()} tickFormatter={(v) => `${v}%`} width={62} />
          <RC.Tooltip {...ssTooltipProps()} formatter={(v, n) => [`${fmtNum(v)}%`, n]} />
          <RC.Legend wrapperStyle={{ fontSize: 12 }} />
          <RC.Line dataKey="revenue_yoy_growth_pct" name="營收年增率 YoY" stroke={SS.s2} strokeWidth={2} dot={false} />
        </RC.LineChart>
      </RC.ResponsiveContainer>
    </ChartCard>
  );
}

function ValuationChart({ symbol, from }) {
  const RC = window.Recharts;
  GxCore.useTheme();
  const st = useAsync(() => qd('tw_stock_valuation_daily', {
    stock_id: symbol, start: from,
    select: 'trading_date,per_tej,pbr_tej,cash_div_yield_pct',
  }).then((s) => qdRows(s, 'trading_date')), [symbol, from]);
  const data = st.data || [];
  // 虧損期標注：per_tej 連續為空的區段 = TEJ 判定近四季虧損。
  // 全期皆空 = 本來就無 EPS（ETF/受益證券），不是虧損 — 文案分開、不畫陰影。
  const perCount = data.filter((r) => r.per_tej != null).length;
  const noEpsAtAll = data.length > 0 && perCount === 0;
  const lossRanges = [];
  if (perCount > 0) {
    let start = null;
    for (const r of data) {
      if (r.per_tej == null) { start = start ?? r.trading_date; }
      else if (start) { lossRanges.push({ x1: start, x2: r.trading_date }); start = null; }
    }
    if (start) lossRanges.push({ x1: start, x2: data[data.length - 1].trading_date });
  }
  const sub = noEpsAtAll
    ? '本檔無本益比（無 EPS，如 ETF / 受益證券）— 圖示淨值比；下：現金殖利率'
    : lossRanges.length
      ? '灰色底 = 近四季虧損期間，該期間無本益比（TEJ 定義）；下：現金殖利率'
      : '本益比（TEJ 版，虧損期為空）與現金殖利率（上下共用時間軸）';
  return (
    <ChartCard title="估值" sub={sub} state={st}>
      <RC.ResponsiveContainer width="100%" height={170}>
        <RC.LineChart data={data} margin={{ top: 4, right: 8, left: 8, bottom: 0 }}>
          <RC.CartesianGrid stroke={GxCore.hex(HR.ruleSoft)} vertical={false} />
          <RC.XAxis dataKey="trading_date" {...ssAxisProps()} minTickGap={60} tickFormatter={fmtDateTick} />
          <RC.YAxis {...ssAxisProps()} width={48} domain={['auto', 'auto']} />
          <RC.Tooltip {...ssTooltipProps()} formatter={(v, n) => [fmtNum(v), n]} />
          <RC.Legend wrapperStyle={{ fontSize: 12 }} />
          {lossRanges.map((g, i) => (
            <RC.ReferenceArea key={i} x1={g.x1} x2={g.x2} fill={GxCore.hex(HR.inkFaint)} fillOpacity={0.13} stroke="none" />
          ))}
          <RC.Line dataKey="per_tej" name="本益比 PER" stroke={SS.s1} strokeWidth={2} dot={false} connectNulls={false} />
          <RC.Line dataKey="pbr_tej" name="股價淨值比 PBR" stroke={SS.s3} strokeWidth={2} dot={false} connectNulls={false} />
        </RC.LineChart>
      </RC.ResponsiveContainer>
      <RC.ResponsiveContainer width="100%" height={120}>
        <RC.LineChart data={data} margin={{ top: 4, right: 8, left: 8, bottom: 0 }}>
          <RC.CartesianGrid stroke={GxCore.hex(HR.ruleSoft)} vertical={false} />
          <RC.XAxis dataKey="trading_date" {...ssAxisProps()} minTickGap={60} tickFormatter={fmtDateTick} />
          <RC.YAxis {...ssAxisProps()} width={48} tickFormatter={(v) => `${v}%`} />
          <RC.Tooltip {...ssTooltipProps()} formatter={(v) => [`${fmtNum(v)}%`, '現金殖利率']} />
          <RC.Line dataKey="cash_div_yield_pct" name="現金殖利率" stroke={SS.s2} strokeWidth={2} dot={false} />
        </RC.LineChart>
      </RC.ResponsiveContainer>
    </ChartCard>
  );
}

function InstFlowChart({ symbol, from }) {
  const RC = window.Recharts;
  GxCore.useTheme();
  const st = useAsync(() => qd('tw_inst_stock_daily', {
    stock_id: symbol, start: from,
    select: 'trading_date,foreign_net_lot,sitc_net_lot,dealer_net_lot,foreign_hold_pct',
  }).then((s) => qdRows(s, 'trading_date')), [symbol, from]);
  const data = st.data || [];
  return (
    <ChartCard title="三大法人" sub="每日買賣超（張）與外資持股比（上下共用時間軸）" state={st}>
      <RC.ResponsiveContainer width="100%" height={190}>
        <RC.BarChart data={data} margin={{ top: 4, right: 8, left: 8, bottom: 0 }} stackOffset="sign" barCategoryGap={0}>
          <RC.CartesianGrid stroke={GxCore.hex(HR.ruleSoft)} vertical={false} />
          <RC.XAxis dataKey="trading_date" {...ssAxisProps()} minTickGap={60} tickFormatter={fmtDateTick} />
          <RC.YAxis {...ssAxisProps()} width={56} tickFormatter={(v) => fmtNum(v, 0)} />
          <RC.Tooltip {...ssTooltipProps()} formatter={(v, n) => [`${fmtNum(v, 0)} 張`, n]} />
          <RC.Legend wrapperStyle={{ fontSize: 12 }} />
          <RC.Bar dataKey="foreign_net_lot" name="外資" stackId="n" fill={SS.s1} />
          <RC.Bar dataKey="sitc_net_lot" name="投信" stackId="n" fill={SS.s2} />
          <RC.Bar dataKey="dealer_net_lot" name="自營" stackId="n" fill={SS.s3} />
        </RC.BarChart>
      </RC.ResponsiveContainer>
      <RC.ResponsiveContainer width="100%" height={120}>
        <RC.LineChart data={data} margin={{ top: 4, right: 8, left: 8, bottom: 0 }}>
          <RC.CartesianGrid stroke={GxCore.hex(HR.ruleSoft)} vertical={false} />
          <RC.XAxis dataKey="trading_date" {...ssAxisProps()} minTickGap={60} tickFormatter={fmtDateTick} />
          <RC.YAxis {...ssAxisProps()} width={56} tickFormatter={(v) => `${v}%`} domain={['auto', 'auto']} />
          <RC.Tooltip {...ssTooltipProps()} formatter={(v) => [`${fmtNum(v)}%`, '外資持股比']} />
          <RC.Line dataKey="foreign_hold_pct" name="外資持股比" stroke={SS.s1} strokeWidth={2} dot={false} />
        </RC.LineChart>
      </RC.ResponsiveContainer>
    </ChartCard>
  );
}

function ChipChart({ symbol, from }) {
  const RC = window.Recharts;
  GxCore.useTheme();
  const st = useAsync(() => qd('tw_chip_dist_daily', {
    stock_id: symbol, start: from,
    select: 'trading_date,pct_over_400,pct_over_1000',
  }).then((s) => qdRows(s, 'trading_date')), [symbol, from]);
  const data = st.data || [];
  return (
    <ChartCard title="占集保庫存數比例 (%)" sub="大戶持股占比（週資料）— >400 張 與 >1000 張級距" state={st}>
      <RC.ResponsiveContainer width="100%" height={220}>
        <RC.LineChart data={data} margin={{ top: 4, right: 8, left: 8, bottom: 0 }}>
          <RC.CartesianGrid stroke={GxCore.hex(HR.ruleSoft)} vertical={false} />
          <RC.XAxis dataKey="trading_date" {...ssAxisProps()} minTickGap={60} tickFormatter={fmtDateTick} />
          <RC.YAxis {...ssAxisProps()} width={48} tickFormatter={(v) => `${v}%`} domain={['auto', 'auto']} />
          <RC.Tooltip {...ssTooltipProps()} formatter={(v, n) => [`${fmtNum(v)}%`, n]} />
          <RC.Legend wrapperStyle={{ fontSize: 12 }} />
          <RC.Line dataKey="pct_over_400" name="大戶 >400 張" stroke={SS.s1} strokeWidth={2} dot={false} />
          <RC.Line dataKey="pct_over_1000" name="千張大戶 >1000 張" stroke={SS.s2} strokeWidth={2} dot={false} />
        </RC.LineChart>
      </RC.ResponsiveContainer>
    </ChartCard>
  );
}

/* ─────────────  Chips cards — margin / broker branches / stock futures (finlab)  ───────────── */

function MarginChart({ symbol, from }) {
  const RC = window.Recharts;
  GxCore.useTheme();
  const st = useAsync(() => qd('tw_margin_daily', {
    stock_id: symbol, start: from,
    select: 'trading_date,margin_balance_lot,short_balance_lot,short_to_margin_pct,margin_util_pct',
  }).then((s) => qdRows(s, 'trading_date')), [symbol, from]);
  const data = st.data || [];
  return (
    <ChartCard title="融資融券" sub="融資／融券餘額（張）；下：券資比與融資使用率（上下共用時間軸）" state={st}>
      <RC.ResponsiveContainer width="100%" height={180}>
        <RC.LineChart data={data} margin={{ top: 4, right: 8, left: 8, bottom: 0 }}>
          <RC.CartesianGrid stroke={GxCore.hex(HR.ruleSoft)} vertical={false} />
          <RC.XAxis dataKey="trading_date" {...ssAxisProps()} minTickGap={60} tickFormatter={fmtDateTick} />
          <RC.YAxis {...ssAxisProps()} width={56} tickFormatter={(v) => fmtNum(v, 0)} />
          <RC.Tooltip {...ssTooltipProps()} formatter={(v, n) => [`${fmtNum(v, 0)} 張`, n]} />
          <RC.Legend wrapperStyle={{ fontSize: 12 }} />
          <RC.Line dataKey="margin_balance_lot" name="融資餘額" stroke={SS.s1} strokeWidth={2} dot={false} />
          <RC.Line dataKey="short_balance_lot" name="融券餘額" stroke={SS.s2} strokeWidth={2} dot={false} />
        </RC.LineChart>
      </RC.ResponsiveContainer>
      <RC.ResponsiveContainer width="100%" height={120}>
        <RC.LineChart data={data} margin={{ top: 4, right: 8, left: 8, bottom: 0 }}>
          <RC.CartesianGrid stroke={GxCore.hex(HR.ruleSoft)} vertical={false} />
          <RC.XAxis dataKey="trading_date" {...ssAxisProps()} minTickGap={60} tickFormatter={fmtDateTick} />
          <RC.YAxis {...ssAxisProps()} width={56} tickFormatter={(v) => `${v}%`} domain={['auto', 'auto']} />
          <RC.Tooltip {...ssTooltipProps()} formatter={(v, n) => [`${fmtNum(v)}%`, n]} />
          <RC.Legend wrapperStyle={{ fontSize: 12 }} />
          <RC.Line dataKey="short_to_margin_pct" name="券資比" stroke={SS.s3} strokeWidth={2} dot={false} />
          <RC.Line dataKey="margin_util_pct" name="融資使用率" stroke={SS.s1} strokeWidth={2} dot={false} />
        </RC.LineChart>
      </RC.ResponsiveContainer>
    </ChartCard>
  );
}

/* 主力分點：上游是富邦/群益/永豐 zco 頁「每日買賣超前 15 名」（每檔每日 30 列），
 * 非全市場明細；爬蟲落後交易日數天 → 卡片如實顯示實際資料期間。
 * side + net_lots 是「方向＋量」→ 彙總前先簽名。 */
const BROKER_WINDOW_DAYS = 60;

function BrokerFlowCard({ symbol }) {
  const st = useAsync(async () => {
    const s = await qd('tw_broker_branch_daily', {
      stock_code: symbol, start: isoDaysAgo(BROKER_WINDOW_DAYS),
      select: 'trade_date,side,broker,net_lots',
    });
    const rows = qdRows(s, 'trade_date');
    if (!rows.length) return { empty: true };
    const agg = new Map();
    for (const r of rows) {
      const signed = r.side === 'sell' ? -(r.net_lots || 0) : (r.net_lots || 0);
      agg.set(r.broker, (agg.get(r.broker) || 0) + signed);
    }
    const all = [...agg.entries()].map(([broker, net]) => ({ broker, net }));
    return {
      buys: all.filter((x) => x.net > 0).sort((a, b) => b.net - a.net).slice(0, 10),
      sells: all.filter((x) => x.net < 0).sort((a, b) => a.net - b.net).slice(0, 10),
      span: [rows[0].trade_date, rows[rows.length - 1].trade_date],
      days: new Set(rows.map((r) => r.trade_date)).size,
    };
  }, [symbol]);
  const d = st.data;

  /* 每列：分點名＋淨買賣超值＋水平長條（長度 = |淨額| ÷ 該欄最大值） */
  const col = (title, items, tone) => {
    const maxAbs = Math.max(...items.map((x) => Math.abs(x.net)), 1);
    return (
      <div style={{ flex: 1, minWidth: 240 }}>
        <div style={{ color: HR.inkFaint, fontSize: FontSizes.xxs, marginBottom: 6 }}>{title}</div>
        {items.length === 0 && <StateNote text="無" />}
        {items.map((x, i) => (
          <div key={x.broker} style={{
            borderTop: i === 0 ? 'none' : `1px solid ${HR.ruleSoft}`, padding: '6px 2px 7px',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, fontSize: FontSizes.xs }}>
              <span style={{ color: HR.ink }}>{x.broker}</span>
              <span style={{ fontFamily: HR.mono, fontVariantNumeric: 'tabular-nums', color: tone, fontWeight: 600, whiteSpace: 'nowrap' }}>
                {`${x.net > 0 ? '+' : ''}${fmtNum(x.net, 0)} 張`}
              </span>
            </div>
            <div style={{ height: 5, borderRadius: 3, background: HR.ruleSoft, marginTop: 4 }}>
              <div style={{
                width: `${Math.max(2, (Math.abs(x.net) / maxAbs) * 100)}%`,
                height: 5, borderRadius: 3, background: tone, opacity: 0.7,
              }} />
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <ChartCard
      title="主力分點買賣超"
      sub={d && d.span
        ? `期間累計 ${d.span[0]} ～ ${d.span[1]}（${d.days} 個交易日）· 上游為每日買賣超前 15 名分點，非全市場明細，且更新落後數個交易日`
        : `近 ${BROKER_WINDOW_DAYS} 曆日 · 上游為每日買賣超前 15 名分點，非全市場明細`}
      state={st}
    >
      {d && (d.empty
        ? <StateNote text="此區間無分點資料（上游僅涵蓋部分個股，且更新落後數日）" />
        : (
          <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
            {col('買超分點（累計淨買超前 10）', d.buys, ssUp())}
            {col('賣超分點（累計淨賣超前 10）', d.sells, ssDown())}
          </div>
        ))}
    </ChartCard>
  );
}

/* Broker concentration: derived upstream from the same zco top-15 branch data —
 * main_net_lots = signed sum of the top-15 branches; conc pct = main net lots /
 * volume (%), every window precomputed upstream (zero frontend math, per the
 * 2026-08-25 ruling); same multi-day crawler lag as BrokerFlowCard. */
function BrokerConcChart({ symbol, from }) {
  const RC = window.Recharts;
  GxCore.useTheme();
  const st = useAsync(() => qd('tw_broker_branch_conc_daily', {
    stock_code: symbol, start: from,
    select: 'trade_date,main_net_lots,broker_net_conc_pct_5d,broker_net_conc_pct_20d,broker_net_conc_pct_60d',
  }).then((s) => qdRows(s, 'trade_date')), [symbol, from]);
  const data = st.data || [];
  return (
    <ChartCard
      title="主力籌碼集中度"
      sub="上：主力（每日買賣超前 15 名分點合計）買賣超（張）；下：5／20／60 日集中度＝主力買賣超÷成交量（%）— 上游更新落後數個交易日"
      state={st}
    >
      <RC.ResponsiveContainer width="100%" height={190}>
        <RC.BarChart data={data} margin={{ top: 4, right: 8, left: 8, bottom: 0 }} barCategoryGap={0}>
          <RC.CartesianGrid stroke={GxCore.hex(HR.ruleSoft)} vertical={false} />
          <RC.XAxis dataKey="trade_date" {...ssAxisProps()} minTickGap={60} tickFormatter={fmtDateTick} />
          <RC.YAxis {...ssAxisProps()} width={56} tickFormatter={(v) => fmtNum(v, 0)} />
          <RC.Tooltip {...ssTooltipProps()} formatter={(v) => [`${fmtNum(v, 0)} 張`, '主力買賣超']} />
          <RC.Legend wrapperStyle={{ fontSize: 12 }} />
          <RC.ReferenceLine y={0} stroke={GxCore.hex(HR.rule)} />
          <RC.Bar dataKey="main_net_lots" name="主力買賣超" fill={SS.s1} />
        </RC.BarChart>
      </RC.ResponsiveContainer>
      <RC.ResponsiveContainer width="100%" height={120}>
        <RC.LineChart data={data} margin={{ top: 4, right: 8, left: 8, bottom: 0 }}>
          <RC.CartesianGrid stroke={GxCore.hex(HR.ruleSoft)} vertical={false} />
          <RC.XAxis dataKey="trade_date" {...ssAxisProps()} minTickGap={60} tickFormatter={fmtDateTick} />
          <RC.YAxis {...ssAxisProps()} width={56} tickFormatter={(v) => `${v}%`} domain={['auto', 'auto']} />
          <RC.Tooltip {...ssTooltipProps()} formatter={(v, n) => [`${fmtNum(v)}%`, n]} />
          <RC.Legend wrapperStyle={{ fontSize: 12 }} />
          <RC.ReferenceLine y={0} stroke={GxCore.hex(HR.rule)} />
          <RC.Line dataKey="broker_net_conc_pct_5d" name="5 日集中度" stroke={SS.s1} strokeWidth={2} dot={false} />
          <RC.Line dataKey="broker_net_conc_pct_20d" name="20 日集中度" stroke={SS.s2} strokeWidth={2} dot={false} />
          <RC.Line dataKey="broker_net_conc_pct_60d" name="60 日集中度" stroke={SS.s3} strokeWidth={2} dot={false} />
        </RC.LineChart>
      </RC.ResponsiveContainer>
    </ChartCard>
  );
}

/* 股票期貨大額交易人：
 * 期交所不公布「個股期貨」的三大法人明細（tw_inst_futures_full_daily 只有指數期），
 * 改用大額交易人的「前十大特定法人」買賣方未平倉替代，副標如實標示。
 * 主契約不能用口數選 — 小型契約（如小型台積電期）口數多但名目小：
 * 用 futures_specs.期貨名稱辨識「小型」字樣，優先非小型，同級再取期末 OI 最大。 */
function StockFuturesCard({ symbol }) {
  const RC = window.Recharts;
  GxCore.useTheme();
  const st = useAsync(async () => {
    const cont = await qd('stock_futures_continuous_d', {
      underlying_code: symbol, start: isoDaysAgo(45),
      select: 'trading_date,futures_code,open_interest',
    });
    const rows = qdRows(cont, 'trading_date');
    if (!rows.length) return { none: true };
    const lastOi = new Map();
    for (const r of rows) lastOi.set(r.futures_code, r.open_interest || 0);
    const codes = [...lastOi.keys()];
    const specNames = await Promise.all(codes.map((c) =>
      qd('futures_specs', { 期貨契約代碼: c, select: '期貨名稱', limit: 1 })
        .then((s) => (s.rows && s.rows[0] && s.rows[0][0]) || '')
        .catch(() => '')));
    const infos = codes.map((c, i) => ({
      code: c,
      oi: lastOi.get(c) || 0,
      // "CDF201002      台積電期 2010/02" → "台積電期"
      name: (specNames[i].replace(/^\S+\s+/, '').replace(/\s+\d{4}\/\d{2}\s*$/, '')) || c,
      mini: /小型/.test(specNames[i]),
    }));
    infos.sort((a, b) => (a.mini - b.mini) || (b.oi - a.oi));
    const main = infos[0];
    const lt = await qd('tw_futures_large_trader_daily', {
      contract_id: `Z${main.code}_A`, start: isoDaysAgo(90),
      select: 'trading_date,total_oi,top10_buy_institutional,top10_sell_institutional',
    });
    return { main, data: qdRows(lt, 'trading_date') };
  }, [symbol]);

  const d = st.data;
  if (d && d.none) {
    return (
      <ChartCard title="股票期貨大額交易人" sub="前十大特定法人買賣方未平倉">
        <StateNote text="本檔無股票期貨" />
      </ChartCard>
    );
  }
  const data = (d && d.data) || [];
  return (
    <ChartCard
      title="股票期貨大額交易人"
      sub={d && d.main
        ? `主契約：${d.main.name}（${d.main.code}）· 期交所不公布個股期貨外資明細，以前十大「特定法人」買賣方未平倉替代`
        : '前十大特定法人買賣方未平倉（近 90 日）'}
      state={st}
    >
      {d && !d.data.length
        ? <StateNote text="查無大額交易人資料" />
        : (
          <>
            <RC.ResponsiveContainer width="100%" height={180}>
              <RC.LineChart data={data} margin={{ top: 4, right: 8, left: 8, bottom: 0 }}>
                <RC.CartesianGrid stroke={GxCore.hex(HR.ruleSoft)} vertical={false} />
                <RC.XAxis dataKey="trading_date" {...ssAxisProps()} minTickGap={60} tickFormatter={fmtDateTick} />
                <RC.YAxis {...ssAxisProps()} width={56} tickFormatter={(v) => fmtNum(v, 0)} />
                <RC.Tooltip {...ssTooltipProps()} formatter={(v, n) => [`${fmtNum(v, 0)} 口`, n]} />
                <RC.Legend wrapperStyle={{ fontSize: 12 }} />
                <RC.Line dataKey="top10_buy_institutional" name="前十大特定法人（買方）" stroke={SS.s1} strokeWidth={2} dot={false} />
                <RC.Line dataKey="top10_sell_institutional" name="前十大特定法人（賣方）" stroke={SS.s2} strokeWidth={2} dot={false} />
              </RC.LineChart>
            </RC.ResponsiveContainer>
            <RC.ResponsiveContainer width="100%" height={110}>
              <RC.LineChart data={data} margin={{ top: 4, right: 8, left: 8, bottom: 0 }}>
                <RC.CartesianGrid stroke={GxCore.hex(HR.ruleSoft)} vertical={false} />
                <RC.XAxis dataKey="trading_date" {...ssAxisProps()} minTickGap={60} tickFormatter={fmtDateTick} />
                <RC.YAxis {...ssAxisProps()} width={56} tickFormatter={(v) => fmtNum(v, 0)} />
                <RC.Tooltip {...ssTooltipProps()} formatter={(v) => [`${fmtNum(v, 0)} 口`, '全市場未平倉']} />
                <RC.Line dataKey="total_oi" name="全市場未平倉" stroke={SS.s3} strokeWidth={2} dot={false} />
              </RC.LineChart>
            </RC.ResponsiveContainer>
          </>
        )}
    </ChartCard>
  );
}

/* ─────────────  K 線 + 成交量（lightweight-charts）─────────────
 * 漲跌配色跟入口網 PnL 語意（Bento 漲=墨綠 HR.up、跌=磚紅 HR.down；Expressive 漲紅跌綠），與交易紀錄頁一致。
 * canvas 不認 var(--gx-*) → 顏色一律經 GxCore.hex()（Bento 原值直通）；換主題時由獨立 effect
 * applyOptions／重設量柱色，不重抓資料（主題不在資料 effect 的 deps 內）。 */
function ssChartThemeOpts() {
  return {
    layout: { background: { color: 'transparent' }, textColor: GxCore.hex(HR.inkFaint), fontSize: 11, fontFamily: HR.sans },
    grid: { vertLines: { color: GxCore.hex(HR.ruleSoft) }, horzLines: { color: GxCore.hex(HR.ruleSoft) } },
    rightPriceScale: { borderColor: GxCore.hex(HR.rule) },
    timeScale: { borderColor: GxCore.hex(HR.rule) },
    crosshair: {
      horzLine: { labelBackgroundColor: GxCore.hex(HR.inkSoft) },
      vertLine: { labelBackgroundColor: GxCore.hex(HR.inkSoft) },
    },
  };
}
function ssCandleThemeOpts() {
  return { upColor: ssUpHex(), downColor: ssDownHex(), wickUpColor: ssUpHex(), wickDownColor: ssDownHex() };
}
function ssVolData(rows, o, c) {
  return rows.map((r) => ({
    time: r.trading_date,
    value: r.volume || 0,
    color: ssVol((r[c] || 0) >= (r[o] || 0)),
  }));
}

function PriceChart({ symbol, from }) {
  const el = React.useRef(null);
  const [adj, setAdj] = React.useState(false);
  const [state, setState] = React.useState('loading'); // loading | ready | empty | error
  const [lastErr, setLastErr] = React.useState(null);
  const [nonce, setNonce] = React.useState(0);
  const theme = GxCore.useTheme();
  const live = React.useRef(null);   // { chart, candles, vol, rows, o, c, theme } of the mounted chart

  React.useEffect(() => {
    const LWC = window.LightweightCharts;
    if (!el.current || !LWC) return;
    setState('loading');
    // ⚠ 不用 autoSize：vendor 版 4.2.3 實測不會跟著容器縮放（與 height 併用時
    // 失效），視窗/佈局一變 canvas 就維持舊寬爆出卡片 → 自己掛 ResizeObserver 控寬。
    const chart = LWC.createChart(el.current, {
      height: 360,
      width: el.current.clientWidth || 600,
      ...ssChartThemeOpts(),
      localization: { locale: 'zh-TW' },
    });

    const candles = chart.addCandlestickSeries({
      upColor: ssUpHex(), downColor: ssDownHex(), borderVisible: false,
      wickUpColor: ssUpHex(), wickDownColor: ssDownHex(),
    });
    const vol = chart.addHistogramSeries({
      priceScaleId: 'vol', priceFormat: { type: 'volume' },
      lastValueVisible: false, priceLineVisible: false,
    });
    chart.priceScale('vol').applyOptions({ scaleMargins: { top: 0.82, bottom: 0 }, borderColor: GxCore.hex(HR.rule) });
    live.current = { chart, candles, vol, rows: null, o: null, c: null, theme: GxCore.theme() };

    const ro = new ResizeObserver(() => {
      if (el.current && el.current.clientWidth > 0) chart.applyOptions({ width: el.current.clientWidth });
    });
    ro.observe(el.current);

    let cancelled = false;
    (async () => {
      try {
        const s = await qd('tw_stock_bars', {
          symbol, start: from,
          select: 'trading_date,open,high,low,close,volume,adj_open,adj_high,adj_low,adj_close',
        });
        if (cancelled) return;
        const rows = qdRows(s, 'trading_date');
        if (rows.length === 0) { setState('empty'); return; }
        const o = adj ? 'adj_open' : 'open', h = adj ? 'adj_high' : 'high',
              l = adj ? 'adj_low' : 'low', c = adj ? 'adj_close' : 'close';
        candles.setData(rows
          .filter((r) => r[o] != null && r[c] != null)
          .map((r) => ({ time: r.trading_date, open: r[o], high: r[h], low: r[l], close: r[c] })));
        vol.setData(ssVolData(rows, o, c));
        if (live.current) Object.assign(live.current, { rows, o, c });
        chart.timeScale().fitContent();
        setState('ready');
      } catch (e) { if (!cancelled) { setLastErr(e || null); setState('error'); } }
    })();

    return () => { cancelled = true; ro.disconnect(); live.current = null; chart.remove(); };
  }, [symbol, from, adj, nonce]);

  // Theme change (Expressive only — Bento is always 'light', so this never fires there): recolour in place.
  React.useEffect(() => {
    const L = live.current;
    if (!L || L.theme === theme) return;
    L.theme = theme;
    L.chart.applyOptions(ssChartThemeOpts());
    L.candles.applyOptions(ssCandleThemeOpts());
    L.chart.priceScale('vol').applyOptions({ borderColor: GxCore.hex(HR.rule) });
    if (L.rows) L.vol.setData(ssVolData(L.rows, L.o, L.c));
  }, [theme]);

  const toggleBtn = (label, isAdj) => (
    <button
      onClick={() => setAdj(isAdj)}
      style={uiStyle({
        border: `1px solid ${adj === isAdj ? HR.accent : HR.rule}`, borderRadius: 6,
        background: adj === isAdj ? HR.accentSoft : HR.surface,
        color: adj === isAdj ? HR.accent : HR.inkSoft,
        padding: '2px 10px', fontSize: FontSizes.xxs, cursor: 'pointer', fontFamily: HR.sans,
      }, ssSegGx(adj === isAdj))}
    >{label}</button>
  );

  return (
    <ChartCard
      title="股價 K 線"
      sub={`日 K + 成交量（${adj ? '還原權息' : '原始價格'}）`}
      right={<div style={{ display: 'flex', gap: 6 }}>{toggleBtn('原始', false)}{toggleBtn('還原權息', true)}</div>}
    >
      {state === 'empty' && <StateNote text="此區間無股價資料" />}
      {state === 'error' && <RetryNote err={lastErr} onRetry={() => setNonce((n) => n + 1)} />}
      {/* overflow:hidden 是保險：RO 若被瀏覽器節流延遲，過寬的舊 canvas 也只會被裁掉、不會爆出卡片 */}
      <div ref={el} style={{ width: '100%', overflow: 'hidden', display: state === 'empty' || state === 'error' ? 'none' : 'block' }} />
    </ChartCard>
  );
}

/* ─────────────  公司分析報告（auto_acr metadata 清單）───────────── */

const MATCH_LABEL = {
  code_in_title: '代碼比對',
  name_in_title: '標題名稱',
  name_in_summary: '摘要提及',
  manual: '人工標記',
};

function ReportList({ symbol }) {
  const st = useAsync(() => fin.reports(symbol).then((r) => (r && r.reports) || []), [symbol]);
  const reports = st.data;
  // 全文站內 Modal（上游回 JSON {markdown, title, ...}，直開新分頁會是生 JSON）
  // viewing: {job_id, title, phase: 'loading'|'ready'|'error', markdown}
  const [viewing, setViewing] = React.useState(null);

  const openReport = (r) => {
    setViewing({ job_id: r.job_id, title: r.title || '(未命名報告)', phase: 'loading', markdown: '' });
    finGet(`/finlab/reports/${r.job_id}?lang=zh`)
      .then((d) => setViewing((v) => (v && v.job_id === r.job_id)
        ? { ...v, phase: 'ready', markdown: (d && d.markdown) || '', title: (d && d.title) || v.title }
        : v))
      .catch(() => setViewing((v) => (v && v.job_id === r.job_id) ? { ...v, phase: 'error' } : v));
  };

  return (
    <ChartCard title="公司分析報告" sub="內部 auto_acr 報告中與本股相關者（依比對信心排序展示）">
      {st.err ? <RetryNote err={st.err} onRetry={st.retry} />
        : reports === null ? <StateNote text="載入中…" />
        : reports.length === 0 ? <StateNote text="尚無相關報告" />
        : reports.map((r, i) => (
          <div key={r.job_id} style={{
            borderTop: i === 0 ? 'none' : `1px solid ${HR.ruleSoft}`,
            padding: '10px 2px',
          }}>
            <div style={{ fontWeight: 600, fontSize: FontSizes.sm }}>
              <a
                href="#" onClick={(e) => { e.preventDefault(); openReport(r); }}
                style={{ color: HR.accent, textDecoration: 'none' }}
                onMouseEnter={(e) => { e.currentTarget.style.textDecoration = 'underline'; }}
                onMouseLeave={(e) => { e.currentTarget.style.textDecoration = 'none'; }}
              >{r.title || '(未命名報告)'}</a>
            </div>
            <div style={{ color: HR.inkFaint, fontSize: FontSizes.xxs, display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 2 }}>
              <span>{r.uploaded_at ? r.uploaded_at.slice(0, 10) : ''}</span>
              {r.uploader && <span>上傳：{r.uploader}</span>}
              <span style={{
                borderRadius: 6, padding: '0 6px', fontSize: FontSizes.trading_lg,
                background: HR.accentSoft, color: HR.accent,
              }}>{MATCH_LABEL[r.matched_by] || r.matched_by} · {(r.confidence * 100).toFixed(0)}%</span>
            </div>
            {r.summary && (
              <div style={{
                color: HR.inkSoft, fontSize: FontSizes.xs, marginTop: 3,
                display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
              }}>{r.summary}</div>
            )}
          </div>
        ))}
      <Modal
        open={!!viewing}
        onClose={() => setViewing(null)}
        title={viewing ? viewing.title : ''}
        subtitle="公司分析報告 · auto_acr"
        size="lg"
      >
        {viewing && viewing.phase === 'loading' && <StateNote text="載入報告全文…" />}
        {viewing && viewing.phase === 'error' && <StateNote text="報告全文載入失敗" tone="err" />}
        {viewing && viewing.phase === 'ready' && (
          viewing.markdown
            ? <MarkdownView className="ar-markdown" markdown={viewing.markdown} />
            : <StateNote text="此報告無全文內容" />
        )}
      </Modal>
    </ChartCard>
  );
}

/* ─────────────  個股頁 — 頁首 / 價格列 / 指標磚 / 期間 / 圖表  ───────────── */

const PERIODS = [
  { k: '1M', days: 31 }, { k: '3M', days: 92 }, { k: '6M', days: 183 },
  { k: '1Y', days: 366 }, { k: '3Y', days: 1096 }, { k: '5Y', days: 1827 },
  { k: 'ALL', days: 0 },
];

function Chip({ children, tone }) {
  const style = tone === 'warn'
    ? { background: '#F5EDE3', color: HR.warn, borderColor: 'transparent', fontWeight: 600 }
    : tone === 'idx'
      ? { background: HR.accentSoft, color: HR.accent, borderColor: HR.accentSoft }
      : { background: HR.surface, color: HR.inkSoft, borderColor: HR.rule };
  // GX：tonal 標籤（mockup .gx-chip，圓角 8、無框）；warn → warn tint、idx → accent tint
  const gx = tone === 'warn' ? GxCore.tone('warn') : tone === 'idx' ? GxCore.tone('accent') : GxCore.tone('neutral');
  return (
    <span style={uiStyle({
      border: '1px solid', borderRadius: 99, padding: '1px 10px',
      fontSize: FontSizes.xxs, whiteSpace: 'nowrap', ...style,
    }, {
      border: 'none', borderRadius: GxT.radius.chip, padding: '3px 10px', fontSize: 12,
      fontWeight: GxT.weight.label, background: gx.bg, color: gx.fg,
    })}>{children}</span>
  );
}

function Tile({ k, v, s, color }) {
  return (
    <div style={uiStyle({
      background: HR.surface, border: `1px solid ${HR.rule}`, borderRadius: 10,
      padding: '10px 14px',
    }, { border: 'none', borderRadius: GxT.radius.card, padding: '14px 16px' })}>
      <div style={uiStyle(
        { color: HR.inkFaint, fontSize: FontSizes.trading_xl },
        { color: GxT.color.onV, fontSize: 13, fontWeight: GxT.weight.medium },
      )}>{k}</div>
      <div style={uiStyle({
        fontSize: FontSizes.lg - 6, fontWeight: 700, marginTop: 2,
        fontFamily: HR.mono, fontVariantNumeric: 'tabular-nums',
        color: color || HR.ink,
      }, { fontFamily: GxT.font, fontSize: 22, fontWeight: GxT.weight.title, marginTop: 4 })}>{v}</div>
      {s && <div style={uiStyle(
        { color: HR.inkFaint, fontSize: FontSizes.trading_lg },
        { marginTop: 2 },
      )}>{s}</div>}
    </div>
  );
}

function StockDetail({ symbol, tab }) {
  const sumSt = useAsync(() => qdSummary(symbol), [symbol]);
  const sum = sumSt.data;
  // 交易所版 PER（全額 EPS）— qdSummary 的估值查詢一併帶回（per_ex：近 14 日最後一筆
  // 非空 per），小字並列消除與看盤軟體對不上的疑問（TEJ 常續 vs 交易所版，如 6770）。
  const perEx = sum && sum.per_ex != null ? { per: sum.per_ex } : null;
  const [period, setPeriod] = React.useState('1Y');
  // 造訪過的分頁集合（render 期 add 是冪等的）：造訪過就 keep-mounted，切回不重抓
  const seenRef = React.useRef(new Set());
  seenRef.current.add(tab);

  const from = React.useMemo(() => {
    const p = PERIODS.find((p) => p.k === period);
    if (!p || !p.days) return undefined;
    const d = new Date(); d.setDate(d.getDate() - p.days);
    return d.toISOString().slice(0, 10);
  }, [period]);

  if (sumSt.err) {
    return sumSt.err.status === 404
      ? <StateNote text={`查無此股票代碼（${symbol}）`} tone="err" />
      : <RetryNote err={sumSt.err} onRetry={sumSt.retry} />;
  }
  if (!sum) return <StateNote text={`載入 ${symbol}…`} />;

  const chg = sum.change_pct;
  return (
    <>
      {/* 頁首：名稱 / 代碼 / 標籤 / 警示 */}
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: '10px 14px', margin: '4px 0 4px' }}>
        <h1 style={uiStyle({
          fontFamily: HR.serif, fontSize: FontSizes.lg + 7, fontWeight: 500,
          margin: 0, color: HR.ink, letterSpacing: '-0.015em',
        }, { fontSize: GxT.size.h1, fontWeight: GxT.weight.display, letterSpacing: '-0.5px', lineHeight: 1.12 })}>{sum.name}</h1>
        <span style={{ color: HR.inkFaint, fontSize: FontSizes.md + 2, fontFamily: HR.mono, fontVariantNumeric: 'tabular-nums' }}>{sum.symbol}</span>
        {sum.market && <Chip>{sum.market === 'twse' ? '上市' : sum.market === 'tpex' ? '上櫃' : sum.market}</Chip>}
        {sum.industry && <Chip>{sum.industry}</Chip>}
        {sum.sub_industry && sum.sub_industry !== sum.industry && <Chip>{sum.sub_industry}</Chip>}
        {sum.is_twn50 && <Chip tone="idx">台灣50</Chip>}
        {sum.is_msci && <Chip tone="idx">MSCI</Chip>}
        {sum.is_hdiv && <Chip tone="idx">高股息</Chip>}
        {sum.is_attention && <Chip tone="warn">⚠ 注意股</Chip>}
        {sum.is_disposition && <Chip tone="warn">⚠ 處置股</Chip>}
        {sum.is_suspended && <Chip tone="warn">⚠ 停牌</Chip>}
        {sum.listing_status === 'delisted' && <Chip tone="warn">已下市</Chip>}
      </div>

      {/* 價格列 */}
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, margin: '2px 0 16px' }}>
        <span style={uiStyle({
          fontSize: FontSizes.lg + 8, fontWeight: 700, fontFamily: HR.mono,
          fontVariantNumeric: 'tabular-nums', color: signColor(chg),
        }, { fontFamily: GxT.font, fontWeight: GxT.weight.display, letterSpacing: '-1px' })}>{fmtNum(sum.close)}</span>
        <span style={{ fontSize: FontSizes.md, fontWeight: 600, color: signColor(chg), fontFamily: HR.mono }}>
          {sum.change != null && chg != null
            ? `${sum.change >= 0 ? '▲' : '▼'} ${fmtNum(Math.abs(sum.change))} (${fmtPct(chg)})`
            : '—'}
        </span>
        <span style={{ color: HR.inkFaint, fontSize: FontSizes.xxs }}>{sum.price_date} 收盤 · 每日盤後更新</span>
      </div>

      {/* 指標摘要磚 — 照分頁出現順序排：基本面（本益比→淨值比→殖利率→月營收）→
          籌碼面（外資→大戶）→ 市值墊底 */}
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
        gap: 10, margin: '4px 0 18px',
      }}>
        {/* per_tej 空值兩義：個股=近四季虧損；ETF/受益證券（無產業別）=本無 EPS。
            小字並列交易所版（全額 EPS）— 消除與看盤軟體對不上的疑問 */}
        <Tile k="本益比（TEJ 常續）"
              v={sum.per_tej != null ? fmtNum(sum.per_tej) : sum.industry ? '虧損' : '—'}
              s={[
                sum.per_tej == null && sum.industry ? '近四季 EPS 為負（TEJ）' : null,
                perEx ? `交易所版 ${fmtNum(perEx.per)}` : null,
              ].filter(Boolean).join(' · ') || undefined} />
        <Tile k="股價淨值比（TEJ）" v={fmtNum(sum.pbr_tej)} />
        <Tile k="現金殖利率" v={fmtPctPlain(sum.cash_div_yield_pct, 2)} />
        <Tile k="最新月營收 YoY" v={fmtPct(sum.latest_revenue_yoy, 1)} color={signColor(sum.latest_revenue_yoy)}
              s={(sum.latest_revenue_month || '').slice(0, 7)} />
        <Tile k="外資近5日買賣超" v={fmtLots(sum.foreign_net_5d)} color={signColor(sum.foreign_net_5d)}
              s={`持股 ${fmtPctPlain(sum.foreign_hold_pct)}`} />
        <Tile k="大戶持股（>400張）" v={fmtPctPlain(sum.pct_over_400)} s={`${sum.chip_date || ''} 集保週資料`} />
        <Tile k="市值" v={fmtTWD(sum.market_cap)} />
      </div>

      {/* 期間選擇 */}
      <div style={{ display: 'flex', gap: 6, margin: '8px 0 14px', flexWrap: 'wrap' }}>
        {PERIODS.map((p) => {
          const active = period === p.k;
          return (
            <button
              key={p.k}
              onClick={() => setPeriod(p.k)}
              style={uiStyle({
                border: `1px solid ${active ? HR.accent : HR.rule}`, borderRadius: 6,
                background: active ? HR.accent : HR.surface,
                color: active ? '#fff' : HR.inkSoft,
                padding: '4px 12px', fontSize: FontSizes.xxs,
                fontWeight: active ? 600 : 400, cursor: 'pointer', fontFamily: HR.sans,
              }, ssSegGx(active))}
            >{p.k === 'ALL' ? '全部' : p.k}</button>
          );
        })}
      </div>

      {/* 常駐區到 K 線為止；以下分頁 */}
      <PriceChart symbol={symbol} from={from} />

      <TabBar symbol={symbol} active={tab} />
      {/* Mount only the current tab on first load (keeps the first paint light);
          切過的分頁 keep-mounted 不重抓；換股由 key={symbol} 整組重掛。
          ⚠ 隱藏不能用 display:none —— recharts ResponsiveContainer 會量到寬 0：
          在隱藏中載完的圖切回來不畫（空白），先畫好再隱藏的圖則在視窗改變後
          留著舊寬度爆出卡片。改用 height:0+overflow:hidden+visibility:hidden，
          面板保持佈局寬度，圖表在背景也能量到正確尺寸並跟著視窗縮放。 */}
      {TABS.map((t) => seenRef.current.has(t.k) && (
        <div key={t.k} style={t.k === tab ? undefined : { height: 0, overflow: 'hidden', visibility: 'hidden' }}>
          {t.k === 'fund' && (
            <>
              <ValuationChart symbol={symbol} from={from} />
              <RevenueChart symbol={symbol} />
            </>
          )}
          {t.k === 'chips' && (
            <>
              <InstFlowChart symbol={symbol} from={from} />
              <MarginChart symbol={symbol} from={from} />
              <BrokerFlowCard symbol={symbol} />
              <BrokerConcChart symbol={symbol} from={from} />
              <ChipChart symbol={symbol} from={from} />
              <StockFuturesCard symbol={symbol} />
            </>
          )}
          {t.k === 'reports' && <ReportList symbol={symbol} />}
        </div>
      ))}
    </>
  );
}

function TabBar({ symbol, active }) {
  return (
    <div style={uiStyle(
      { display: 'flex', gap: 2, borderBottom: `1px solid ${HR.rule}`, margin: '20px 0 16px', flexWrap: 'wrap' },
      { gap: 8, borderBottom: 'none', margin: '24px 0 16px' },
    )}>
      {TABS.map((t) => {
        const on = t.k === active;
        return (
          <a
            key={t.k}
            href={tabHash(symbol, t.k)}
            style={uiStyle({
              padding: '8px 16px', textDecoration: 'none',
              color: on ? HR.accent : HR.inkSoft,
              fontWeight: on ? 700 : 400, fontSize: FontSizes.sm, fontFamily: HR.sans,
              borderBottom: `2px solid ${on ? HR.accent : 'transparent'}`,
              marginBottom: -1, whiteSpace: 'nowrap',
            }, {
              // M3 chips 型分頁（與 primitives Tabs 的 GX 版一致）
              display: 'inline-flex', alignItems: 'center', minHeight: 40, padding: '0 16px',
              borderRadius: GxT.radius.btn, borderBottom: 'none', marginBottom: 0,
              fontWeight: GxT.weight.label, fontSize: 14,
              background: on ? GxT.color.sc : 'transparent',
              color: on ? GxT.color.onSc : GxT.color.onV,
            })}
            onMouseEnter={(e) => { if (!on) e.currentTarget.style.color = HR.accent; }}
            onMouseLeave={(e) => { if (!on) e.currentTarget.style.color = HR.inkSoft; }}
          >{t.label}</a>
        );
      })}
    </div>
  );
}

/* ─────────────  頁面殼 — hash 第二段決定首頁/個股頁  ───────────── */

function StockStudioPage() {
  const [route, setRoute] = React.useState(parseRoute);
  React.useEffect(() => {
    const onHash = () => {
      const next = parseRoute();
      setRoute((prev) => {
        // 只有換股票才回頂部；切分頁維持捲動位置
        if (next.symbol !== prev.symbol) window.scrollTo(0, 0);
        return next;
      });
    };
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);
  const symbol = route.symbol;

  // 圖表庫沒載到（vendor script 404 等）就別讓整頁白屏
  if (!window.Recharts || !window.LightweightCharts) {
    return (
      <>
        <PageHeader eyebrow="分析工具 · Stock Studio" title="個股工作室" italic="台股個股行情 · 估值 · 籌碼 · 研究報告" />
        <StateNote text="圖表元件載入失敗，請重新整理；若持續發生請聯繫管理員。" tone="err" />
      </>
    );
  }

  if (!symbol) return <StudioHome />;

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 18, flexWrap: 'wrap' }}>
        <a
          href="#/stock-studio"
          style={uiStyle({
            color: HR.inkFaint, fontSize: FontSizes.xxs, textDecoration: 'none',
            letterSpacing: '0.14em', textTransform: 'uppercase', whiteSpace: 'nowrap',
          }, {
            display: 'inline-flex', alignItems: 'center', minHeight: 40,
            fontSize: 13, fontWeight: GxT.weight.label, letterSpacing: 0, textTransform: 'none',
          })}
          onMouseEnter={(e) => { e.currentTarget.style.color = HR.accent; }}
          onMouseLeave={(e) => { e.currentTarget.style.color = HR.inkFaint; }}
        >← Stock Studio</a>
        <StockSearchBox compact />
      </div>
      {/* key=symbol：換股整組重掛（分頁造訪紀錄、期間、所有卡片狀態一起重置） */}
      <StockDetail key={symbol} symbol={symbol} tab={route.tab} />
      <div style={{ color: HR.inkFaint, fontSize: FontSizes.trading_lg, textAlign: 'center', marginTop: 30 }}>
        資料來源：公司量化資料庫（quantdata）· TEJ / 集保 · 每日盤後同步 · 內部系統，資料僅供內部研究參考
      </div>
    </>
  );
}

window.StockStudioPage = StockStudioPage;
// 供分析工作台（hr-analyst-hub.jsx）重用個股快查與熱門捷徑 — 勿複製一份。
window.StockSearchBox = StockSearchBox;
window.HOT_STOCKS = HOT_STOCKS;
// 供 ⌘K 面板（hr-cmdk.jsx「股票」群組）重用同一份 security_attrs 全表快取與排序 —
// 勿另抓一份。search(q) → Promise<{results: [{symbol, name, industry, listing_status}]}>（前 12 筆）。
window.StockStudioSearch = { search: qdSearch };

})();
